import { FC } from 'react';
import styled, { css } from 'styled-components';

import LocalizedLink from '@/components/LocalizedLink/LocalizedLink';

interface Props {
	text?: string;
	href?: string;
	width?: 'default' | 'wide' | 'big' | 'small';
}

const PrimaryButton: FC<Props> = ({ text, href, width = 'default' }) => (
	<ButtonWrapper $buttonWidth={width}>
		<Button $buttonWidth={width}>
			<LinkWrapper>
				<LocalizedLink href={href ?? ''}>
					<CustomLink>
						<span>{text}</span>

						<ImageWrapper>
							<img alt="CTA Arrow" src="/images/icons/cta-arrow.svg" />
						</ImageWrapper>
					</CustomLink>
				</LocalizedLink>
			</LinkWrapper>

			<AnimatedGradient />
		</Button>
	</ButtonWrapper>
);

const AnimatedGradient = styled.span`
	display: block;
	position: absolute;
	width: 100%;
	height: 100%;
	background: linear-gradient(
		180deg,
		rgba(115, 96, 242, 0) 0%,
		var(--color-purple-70) 100%
	);
	transform: translateY(100%);
	z-index: 2200;
	transition: 0.3s linear;
`;

const ImageWrapper = styled.span`
	margin-right: 28px;
	margin-left: 10px;
	position: relative;
	width: 26px;
	height: 26px;
	background-color: var(--color-white);
	border-radius: 50%;
	transition: 0.4s;

	img {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		transition: 0.4s;
	}
`;

const LinkWrapper = styled.span`
	position: relative;
	z-index: 2300;

	@media (max-width: 530px) {
		width: 100%;
	}
`;

const CustomLink = styled.div`
	display: flex;
	align-items: center;
	text-decoration: none;
	color: var(--color-white);
	padding: 21px 0;
	padding-left: 33px;

	@media (max-width: 530px) {
		justify-content: center;
		padding: 16px 0 !important;
		padding-left: 30px !important;
	}
`;

const ButtonWrapper = styled.button<{ $buttonWidth?: string }>`
	position: relative;
	background: none;
	border: none;
	outline: none;

	${({ $buttonWidth }) =>
		$buttonWidth === 'wide' &&
		css`
			${LinkWrapper} {
				width: 100%;
			}

			${CustomLink} {
				justify-content: center;
				padding-left: 30px !important;
			}
		`};

	@media (max-width: 500px) {
		width: 100%;
	}

	&:after {
		content: '';
		z-index: 30;
		position: absolute;
		width: 146px;
		height: 50px;
		bottom: -10px;
		left: 50%;
		transform: translateX(-50%);
		background-color: var(--color-blue);
		opacity: 0.3;
		filter: blur(32px);
		border-radius: 20px;

		@media (max-width: 600px) {
			opacity: 0;
			display: none;
		}
	}

	&:before {
		content: '';
		z-index: 30;
		position: absolute;
		width: 146px;
		height: 50px;
		bottom: -10px;
		left: 50%;
		transform: translateX(-50%);
		opacity: 0.3;
		background-color: var(--color-blue);
		opacity: 0.2;
		filter: blur(32px);
		border-radius: 20px;

		@media (max-width: 600px) {
			opacity: 0;
			display: none;
		}
	}
`;

const Button = styled.span<{ $buttonWidth?: string }>`
	display: flex;
	overflow: hidden;
	align-items: center;
	position: relative;
	cursor: pointer;
	font-family: var(--font-sans);
	font-weight: var(--font-bold);
	font-size: 14px;
	line-height: 26px;
	text-transform: uppercase;
	background: linear-gradient(
		180deg,
		var(--color-purple) 0%,
		var(--color-purple-80) 100%
	);
	--smooth-corners: 11.34, 3.6;
	mask-image: paint(smooth-corners);

	z-index: 100;

	@media (max-width: 530px) {
		font-size: 13px;
	}

	${({ $buttonWidth }) =>
		$buttonWidth === 'wide' &&
		css`
			justify-content: center;
			mask-image: paint(smooth-corners);
			--smooth-corners: 17, 4;
		`}

	span {
		display: block;
	}

	&:hover {
		${AnimatedGradient} {
			transform: translateY(0);
		}

		${ImageWrapper} {
			transform: translateX(4px) scale(1.1);

			img {
				transform: translate(-50%, -50%) scale(0.9);
			}
		}
	}

	@media (max-width: 530px) {
		--smooth-corners: 23, 5;
		mask-image: paint(smooth-corners);
	}
`;

export default PrimaryButton;
